/**
 * Contract v2 status vocabulary — the SINGLE source of truth for the 7 `envelope_status`
 * values, their canonical display strings, and their AA-safe badge tokens (CB-7.1).
 *
 * Why this module exists (UX-DR20): every status string rendered anywhere in the contracts
 * tracking surface MUST flow through `ENVELOPE_STATUS_META` — never an inline literal — so the
 * vocabulary is identical in the list (CB-7.1), the detail view + timeline (CB-7.2/7.3), and
 * any future surface. The badge component (`ContractStatusBadge.tsx`) is the only renderer.
 *
 * Tokens are verbatim from DESIGN.md `status_draft`…`status_expired` (AA-safe construction):
 * each badge = light **tint** background + dark **ink** text + a colored **accent** icon. The
 * strong color is the icon/accent only — never white-on-color, never color-as-only-cue (each
 * status also has a distinct icon + label). Values are stored in the DB as lowercase-snake under
 * CHECK (no PG enums, AD-2 / Enforcement Rule 2) — displayed via this map only.
 *
 * [Source: DESIGN.md#Style Guide tokens lines 32–38, 71; architecture.md#Naming & Conventions]
 */

import type { Contract, ContractSigner } from '@/types/contract';

/** The 7 v2 envelope statuses, in lifecycle order (CB-1.3, CHECK-constrained lowercase-snake). */
export const ENVELOPE_STATUSES = [
  'draft',
  'sent',
  'in_progress',
  'completed',
  'declined',
  'voided',
  'expired',
] as const;

export type EnvelopeStatus = (typeof ENVELOPE_STATUSES)[number];

export interface EnvelopeStatusMeta {
  /** Canonical display string — the ONLY status text used in the UI (UX-DR20). */
  label: string;
  /** Icon color (the single strong color in the badge). */
  accent: string;
  /** Light tint background. */
  tint: string;
  /** Dark ink text. */
  text: string;
  /** `draft` renders a dashed border. */
  dashedBorder?: boolean;
  /** `voided` renders the contract title struck through. */
  strikethroughTitle?: boolean;
  /** `in_progress` pairs the badge with a per-recipient progress bar. */
  showProgress?: boolean;
}

/**
 * The per-status AA-safe token treatments (DESIGN.md lines 32–38). Display strings here are the
 * canonical badge vocabulary; the only place status text originates.
 */
export const ENVELOPE_STATUS_META: Record<EnvelopeStatus, EnvelopeStatusMeta> = {
  draft: { label: 'Draft', accent: '#6B7280', tint: '#F3F4F6', text: '#374151', dashedBorder: true },
  sent: { label: 'Sent', accent: '#CA8A04', tint: '#FEF9C3', text: '#713F12' },
  in_progress: { label: 'In progress', accent: '#EA580C', tint: '#FFEDD5', text: '#7C2D12', showProgress: true },
  completed: { label: 'Completed', accent: '#16A34A', tint: '#DCFCE7', text: '#14532D' },
  declined: { label: 'Declined', accent: 'var(--destructive)', tint: '#FEE2E2', text: '#7F1D1D' },
  voided: { label: 'Voided', accent: '#9CA3AF', tint: '#F3F4F6', text: '#4B5563', strikethroughTitle: true },
  expired: { label: 'Expired', accent: '#DC2626', tint: '#FEE2E2', text: '#7F1D1D' },
};

const ENVELOPE_STATUS_SET: ReadonlySet<string> = new Set(ENVELOPE_STATUSES);

/** Type guard for the 7 v2 envelope statuses. */
export function isEnvelopeStatus(value: string | null | undefined): value is EnvelopeStatus {
  return value != null && ENVELOPE_STATUS_SET.has(value);
}

/**
 * Resolve the badge meta for a status string, or `null` if it is not one of the 7 v2 statuses
 * (legacy v1 status strings, null, and unknown values all return null — callers fall back to the
 * legacy display path; they MUST NOT force legacy rows into the v2 7-status set).
 */
export function getEnvelopeStatusMeta(status: string | null | undefined): EnvelopeStatusMeta | null {
  return isEnvelopeStatus(status) ? ENVELOPE_STATUS_META[status] : null;
}

/** A contract is v2 iff `contract_model === 'v2'`; absent/legacy ⇒ legacy (AD-1, CB-1.6). */
export function isV2Contract(contract: Pick<Contract, 'contract_model'>): boolean {
  return contract.contract_model === 'v2';
}

/** Source-badge display strings (FR-68, UX-DR9): v2 contracts are "New", legacy are Google Docs. */
export const SOURCE_BADGE_LABELS = {
  v2: 'New',
  legacy: 'Google Doc · legacy',
} as const;

/** The source badge to show for a contract: "New" for v2, "Google Doc · legacy" otherwise. */
export function getSourceBadgeLabel(contract: Pick<Contract, 'contract_model'>): string {
  return isV2Contract(contract) ? SOURCE_BADGE_LABELS.v2 : SOURCE_BADGE_LABELS.legacy;
}

/** Per-recipient signing progress derived from `contract_signers` (CC recipients never sign). */
export interface SignerProgress {
  signed: number;
  total: number;
}

/**
 * Count signing progress over the *signer* recipients only (recipient_type === 'signer'); CC
 * recipients are excluded from the denominator. `signed` counts signer_status === 'signed'.
 */
export function getSignerProgress(signers: ContractSigner[] | undefined | null): SignerProgress {
  const list = (signers ?? []).filter((s) => s.recipient_type === 'signer');
  return {
    signed: list.filter((s) => s.signer_status === 'signed').length,
    total: list.length,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Per-recipient status vocabularies (CB-7.2 — detail-view mini-badges)
// ────────────────────────────────────────────────────────────────────────────

/** A small AA-safe badge treatment: label + tint/ink/accent, optional warning flag. */
export interface StatusMeta {
  label: string;
  accent: string;
  tint: string;
  text: string;
  /** A state the sender should notice (e.g. a bounced delivery). */
  flagged?: boolean;
}

/** Per-signer status values (CB-1.4, CHECK-constrained lowercase-snake). */
export const SIGNER_STATUSES = ['pending', 'viewed', 'signed', 'declined'] as const;
export type SignerStatus = (typeof SIGNER_STATUSES)[number];

/** AA-safe mini-badge tokens for each per-signer status (icon+text+tint, never color-alone). */
export const SIGNER_STATUS_META: Record<SignerStatus, StatusMeta> = {
  pending: { label: 'Pending', accent: '#6B7280', tint: '#F3F4F6', text: '#374151' },
  viewed: { label: 'Viewed', accent: '#7C3AED', tint: '#F3E8FF', text: '#581C87' },
  signed: { label: 'Signed', accent: '#16A34A', tint: '#DCFCE7', text: '#14532D' },
  declined: { label: 'Declined', accent: 'var(--destructive)', tint: '#FEE2E2', text: '#7F1D1D' },
};

const SIGNER_STATUS_SET: ReadonlySet<string> = new Set(SIGNER_STATUSES);

/** Resolve per-signer status mini-badge meta, or null for unknown/absent values. */
export function getSignerStatusMeta(status: string | null | undefined): StatusMeta | null {
  return status != null && SIGNER_STATUS_SET.has(status)
    ? SIGNER_STATUS_META[status as SignerStatus]
    : null;
}

/** Per-recipient delivery status values (CB-4.3, from Postmark webhooks). */
export const DELIVERY_STATUSES = ['sent', 'delivered', 'opened', 'bounced'] as const;
export type DeliveryStatusValue = (typeof DELIVERY_STATUSES)[number];

/**
 * AA-safe delivery-status tokens. `bounced` is `flagged` (FR-39): the sender must see the invite
 * didn't land. sent/delivered/opened are quieter progressive states.
 */
export const DELIVERY_STATUS_META: Record<DeliveryStatusValue, StatusMeta> = {
  sent: { label: 'Sent', accent: '#9CA3AF', tint: '#F3F4F6', text: '#4B5563' },
  delivered: { label: 'Delivered', accent: '#0891B2', tint: '#CFFAFE', text: '#155E75' },
  opened: { label: 'Opened', accent: '#7C3AED', tint: '#F3E8FF', text: '#581C87' },
  bounced: { label: 'Bounced', accent: 'var(--destructive)', tint: '#FEE2E2', text: '#7F1D1D', flagged: true },
};

const DELIVERY_STATUS_SET: ReadonlySet<string> = new Set(DELIVERY_STATUSES);

/** Resolve delivery-status indicator meta, or null for unknown/absent values. */
export function getDeliveryStatusMeta(status: string | null | undefined): StatusMeta | null {
  return status != null && DELIVERY_STATUS_SET.has(status)
    ? DELIVERY_STATUS_META[status as DeliveryStatusValue]
    : null;
}

/** A map of contract_id → its signer rows (the shape held in the list's signer state). */
export type SignersByContract = Record<string, ContractSigner[]>;

/** Group a flat batched `contract_signers` read into a contract_id → signers map (preserves order). */
export function groupSignersByContract(rows: ContractSigner[]): SignersByContract {
  const map: SignersByContract = {};
  for (const row of rows) {
    (map[row.contract_id] ??= []).push(row);
  }
  return map;
}

/**
 * Immutably fold a single realtime `contract_signers` payload into the signers map: update the
 * matching signer in place (by id), or append it (creating the contract bucket if absent). Returns
 * a new map so React state updates trigger a re-render. Mirrors the in-place/prepend reconcile the
 * `contracts` subscription already uses.
 */
export function reconcileSignerIntoMap(
  map: SignersByContract,
  signer: ContractSigner
): SignersByContract {
  const existing = map[signer.contract_id] ?? [];
  const idx = existing.findIndex((s) => s.id === signer.id);
  const nextList = idx >= 0
    ? existing.map((s) => (s.id === signer.id ? signer : s))
    : [...existing, signer];
  return { ...map, [signer.contract_id]: nextList };
}

/**
 * Immutably remove a signer (by id) from the map — used for realtime DELETE events, whose payload
 * carries only the row's primary key in `old` (no contract_id), so we scan buckets by id. Returns
 * a new map only if something was removed; otherwise the original reference (no needless re-render).
 */
export function removeSignerFromMap(
  map: SignersByContract,
  signerId: string
): SignersByContract {
  for (const [contractId, list] of Object.entries(map)) {
    if (list.some((s) => s.id === signerId)) {
      return { ...map, [contractId]: list.filter((s) => s.id !== signerId) };
    }
  }
  return map;
}
